import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  query,
  where,
  getDocs,
} from '@angular/fire/firestore';
import { Observable, from, of, combineLatest } from 'rxjs';
import { map, switchMap, shareReplay, tap, catchError } from 'rxjs/operators';
import { DemoVendorService } from './demo-vendor.service';

export interface Equipment {
  id?: string;
  name: string;
  vehicleName?: string;
  listingName?: string;
  brand: string;
  year_of_manufacture: string;
  model: string;
  emirate: string;
  dailyRate: number;
  weeklyRate?: number;
  monthlyRate?: number;
  tripRate?: number;
  perCubicMetreRate?: number;
  hasDailyRate?: boolean;
  hasWeeklyRate?: boolean;
  hasMonthlyRate?: boolean;
  hasTripRate?: boolean;
  hasPerCubicMetreRate?: boolean;
  images: string[];
  withFuel?: string;
  withDriver?: string;
  avgRating?: number;
  totalReviews?: number;
  status?: string;
  isDemo?: boolean;
  bywhom?: string;
}

export interface Category {
  id: string;
  name: string;
  capacityLabel: string;
  status: boolean;
  imageUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class LandingService {
  private equipmentCache$: Observable<Equipment[]> | null = null;
  private categoriesCache$: Observable<Category[]> | null = null;

  constructor(
    private afs: Firestore,
    private demoVendorService: DemoVendorService
  ) { }

  /**
   * Fetches categories and caches the result.
   */
  getCategories(): Observable<Category[]> {
    if (!this.categoriesCache$) {
      const categoryRef = collection(this.afs, 'vehicle-categories');
      this.categoriesCache$ = (
        collectionData(categoryRef, { idField: 'id' }) as Observable<Category[]>
      ).pipe(
        map((list) => list.filter((cat) => cat.status === true)),
        shareReplay(1),
      );
    }
    return this.categoriesCache$;
  }

  /**
   * Fetches all active equipment, computes ratings for each, and caches the result.
   * Only shows equipment that has been paid-activated on the marketplace:
   *   - status === 'Active'
   *   - isMarketplaceActive === true
   *   - listingExpiry is present and has not yet passed
   */
  getAllEquipmentsWithRatings(): Observable<Equipment[]> {
    if (!this.equipmentCache$) {
      const equipmentsRef = collection(this.afs, 'equipment-list');
      const q = query(
        equipmentsRef,
        where('status', '==', 'Active'),
        where('isMarketplaceActive', '==', true),
      );
      const equipments$ = collectionData(q, { idField: 'id' }) as Observable<Equipment[]>;

      this.equipmentCache$ = equipments$.pipe(
        map((list) => list),
        switchMap((list) => from(this.loadRatingsForList(list))),
        shareReplay(1),
      );
    }
    return this.equipmentCache$;
  }

  /**
   * Attach ratings to equipment list.
   * FAST PATH: uses avgRating/totalReviews already stored on the document (no extra reads).
   * SLOW PATH: only hits the reviews sub-collection when those fields are absent.
   */
  private async loadRatingsForList(list: Equipment[]): Promise<Equipment[]> {
    const withRatings: Equipment[] = [];
    const needsFetch: Equipment[] = [];

    for (const eq of list as any[]) {
      if (eq.avgRating !== undefined && eq.totalReviews !== undefined) {
        withRatings.push(eq);
      } else {
        needsFetch.push(eq);
      }
    }

    const fetched = await Promise.all(
      needsFetch.map(async (equip: any) => {
        const rate = await this.getEquipmentRating(equip.id!);
        return { ...equip, avgRating: rate.avg, totalReviews: rate.count };
      })
    );

    const merged = [...withRatings, ...fetched];
    return await this.demoVendorService.attachDemoFlags(merged);
  }

  /**
   * Fetches rating for a single equipment.
   */
  private async getEquipmentRating(
    equipmentId: string,
  ): Promise<{ avg: number; count: number }> {
    try {
      // Note: Ideally, this should be an aggregation query or stored on the document
      // to avoid reading all review documents.
      const revRef = collection(this.afs, `reviews/${equipmentId}/items`);
      const snap = await getDocs(revRef);

      let total = 0;
      let count = 0;

      snap.forEach((d) => {
        const data: any = d.data();
        if (data.rating) {
          total += data.rating;
          count++;
        }
      });

      const avg = count > 0 ? Number((total / count).toFixed(1)) : 0;
      return { avg, count };
    } catch (err) {
      console.error('Error loading rating for', equipmentId, err);
      return { avg: 0, count: 0 };
    }
  }

  /**
   * Returns equipment grouped by Emirate (Top 5 per Emirate).
   */
  getEquipmentsByEmirate(): Observable<
    { emirate: string; equipments: Equipment[] }[]
  > {
    return this.getAllEquipmentsWithRatings().pipe(
      map((allEquipments) => {
        const order = ['Abu Dhabi', 'Dubai', 'Sharjah'];
        return order.map((emirate) => ({
          emirate,
          equipments: allEquipments
            .filter(
              (e) => e.emirate?.trim().toLowerCase() === emirate.toLowerCase(),
            )
            .slice(0, 5),
        }));
      }),
    );
  }

  /**
   * Returns top 6 most demanded equipment (currently just the first 6).
   */
  getMostDemanded(): Observable<Equipment[]> {
    return this.getAllEquipmentsWithRatings().pipe(
      map((allEquipments) => allEquipments.slice(0, 6)),
    );
  }
}
